import { apiUrl } from "../../../../../Constants";

export const PASSENGER_DOCUMENT_DEFINITIONS = [
  {
    id: 1,
    documentType: "Proof of address",
    documentTypeAlias: "PROOF_OF_ADDRESS",
  },
  {
    id: 2,
    documentType: "A valid driver's license",
    documentTypeAlias: "VALID_DRIVERS_LICENSE",
  },
  {
    id: 3,
    documentType: "Taxpayer's Card",
    documentTypeAlias: "TAXPAYERS_CARD",
  },
  {
    id: 4,
    documentType: "Business Registration Certificate",
    documentTypeAlias: "BUSINESS_REGISTRATION_CERTIFICATE",
  },
  {
    id: 5,
    documentType: "Vehicle Registration Document",
    documentTypeAlias: "VEHICLE_REGISTRATION_DOCUMENT",
  },
  {
    id: 6,
    documentType: "Driver's License",
    documentTypeAlias: "DRIVERS_LICENSE",
  },
  {
    id: 7,
    documentType: "Proof of vehicle insurance",
    documentTypeAlias: "PROOF_OF_VEHICLE_INSURANCE",
  },
  {
    id: 8,
    documentType: "Capacity Certificate",
    documentTypeAlias: "CAPACITY_CERTIFICATE",
  },
  {
    id: 9,
    documentType: "A valid national ID or passport",
    documentTypeAlias: "NATIONAL_ID_OR_PASSPORT",
  },
];

const normalizeDocumentDefinitions = (definitions = []) => {
  const sourceDefinitions =
    Array.isArray(definitions) && definitions.length > 0
      ? definitions
      : PASSENGER_DOCUMENT_DEFINITIONS;

  return sourceDefinitions
    .map((definition, index) => {
      if (!definition || typeof definition !== "object") {
        return null;
      }

      const documentType =
        definition?.documentType ||
        definition?.name ||
        definition?.documentTypeAlias ||
        "";

      return {
        ...definition,
        id: definition?.id || definition?._id || index + 1,
        documentType,
        documentTypeAlias:
          definition?.documentTypeAlias || definition?.alias || normalizeKey(documentType),
      };
    })
    .filter(Boolean);
};

export const normalizeKey = (value) =>
  (value ?? "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const resolveMediaUrl = (value) => {
  if (!value) {
    return "";
  }

  const stringValue = value.toString();

  if (
    stringValue.startsWith("http://") ||
    stringValue.startsWith("https://") ||
    stringValue.startsWith("data:") ||
    stringValue.startsWith("blob:")
  ) {
    return stringValue;
  }

  return `${apiUrl}${stringValue}`;
};

export const extractFileName = (value) => {
  if (!value) {
    return "";
  }

  const safeValue = value.toString().split("?")[0];
  const segments = safeValue.split("/");
  return segments[segments.length - 1] || "";
};

export const isBlobUrl = (value) =>
  typeof value === "string" && value.startsWith("blob:");

const parseDocumentCollection = (details = {}) => {
  const candidates = [
    details?.documents,
    details?.passengerDocuments,
    details?.customerDocuments,
    details?.documentDetails,
    details?.documentList,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (typeof candidate === "string" && candidate.trim()) {
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (error) {
        // Ignore invalid JSON payloads and fall back to single-document fields.
      }
    }
  }

  return [];
};

const findMatchingCollectionDocument = (definition, collection = []) => {
  const normalizedDefinition = normalizeKey(
    definition?.documentTypeAlias || definition?.documentType
  );

  return collection.find((item) => {
    const candidates = [
      item?.documentTypeAlias,
      item?.documentType,
      item?.document_type,
      item?.documentDescription,
      item?.documentTypeId,
      item?.document_type_id,
      item?.alias,
      item?.type,
      item?.name,
    ];

    return candidates.some(
      (candidate) => normalizeKey(candidate) === normalizedDefinition
    );
  });
};

const resolveSingleDocumentForDefinition = (definition, details = {}) => {
  const singleDocumentType =
    details?.documentType ||
    details?.document_type ||
    details?.documentTypeAlias ||
    details?.document_alias ||
    "";

  const normalizedType = normalizeKey(singleDocumentType);
  const normalizedDefinition = normalizeKey(
    definition?.documentTypeAlias || definition?.documentType
  );

  if (!singleDocumentType) {
    return definition?.id === 1
      ? {
          id: details?.documentId || details?.id || definition.id,
          documentPath:
            details?.document ||
            details?.passengerDocument ||
            details?.document_local ||
            details?.documentUrl ||
            details?.document_file ||
            details?.file ||
            details?.filePath ||
            "",
          status: details?.documentStatus || details?.document_status || details?.status,
          fileName: details?.documentName || details?.document_name,
          mimeType: details?.documentMimeType || details?.document_mime_type,
        }
      : null;
  }

  if (normalizedType !== normalizedDefinition) {
    return null;
  }

  return {
    id: details?.documentId || details?.id || definition.id,
    documentPath:
      details?.document ||
      details?.passengerDocument ||
      details?.document_local ||
      details?.documentUrl ||
      details?.document_file ||
      details?.file ||
      details?.filePath ||
      "",
    status: details?.documentStatus || details?.document_status || details?.status,
    fileName: details?.documentName || details?.document_name,
    mimeType: details?.documentMimeType || details?.document_mime_type,
  };
};

const inferMimeType = (fileNameOrUrl = "") => {
  const lowerValue = fileNameOrUrl.toString().toLowerCase();

  if (lowerValue.endsWith(".pdf")) {
    return "application/pdf";
  }

  if (
    lowerValue.endsWith(".jpg") ||
    lowerValue.endsWith(".jpeg") ||
    lowerValue.endsWith(".png") ||
    lowerValue.endsWith(".gif") ||
    lowerValue.endsWith(".webp")
  ) {
    return "image/*";
  }

  return "";
};

export const buildEmptyPassengerDocuments = (definitions = []) =>
  normalizeDocumentDefinitions(definitions).map((definition) => ({
    ...definition,
    documentId: definition.id,
    file: "",
    fileName: "",
    mimeType: "",
    status: "NO DOCUMENT",
    rawFile: null,
  }));

export const normalizePassengerDocuments = (
  details = {},
  definitions = [],
  uploadedDocuments = []
) => {
  const documentDefinitions = normalizeDocumentDefinitions(definitions);
  const collection = [
    ...(Array.isArray(uploadedDocuments) ? uploadedDocuments : []),
    ...parseDocumentCollection(details),
  ];

  return documentDefinitions.map((definition) => {
    const collectionMatch = findMatchingCollectionDocument(definition, collection);
    const singleMatch = resolveSingleDocumentForDefinition(definition, details);
    const match = collectionMatch || singleMatch || {};

    const fileUrl = resolveMediaUrl(
      match?.documentPath ||
        match?.file ||
        match?.documentUrl ||
        match?.document ||
        match?.path ||
        match?.filePath ||
        match?.localPath ||
        ""
    );

    const hasFile = Boolean(fileUrl);

    return {
      ...definition,
      documentId: match?.id || match?.documentId || definition.id,
      file: fileUrl,
      fileName:
        match?.fileName ||
        match?.documentName ||
        match?.uploadedDocumentName ||
        extractFileName(fileUrl) ||
        definition.documentType,
      mimeType: match?.mimeType || inferMimeType(fileUrl),
      status:
        match?.status || match?.documentStatus || (hasFile ? "ACCESSING" : "NO DOCUMENT"),
      rawFile: null,
    };
  });
};
